import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { api } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import { useLanguage } from '../utils/i18n';
import { useCurrency } from '../utils/useCurrency';
import Logo from '../components/Logo';
import LanguageToggle from '../components/LanguageToggle';
import { Check, ArrowLeft, Loader2, AlertTriangle } from 'lucide-react';

export default function Pricing() {
    const { business, session, subscriptionStatus, needsMigration, isReturningUser, refetchBusiness, signOut, loading: authLoading } = useAuth();
    const { t, lang } = useLanguage();
    const currencySymbol = useCurrency();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [checkoutError, setCheckoutError] = useState('');
    const businessReady = !!business?.id;

    // If no session at all, redirect to login
    useEffect(() => {
        if (!authLoading && !session) {
            navigate('/login', { replace: true });
        }
    }, [authLoading, session, navigate]);

    // If already subscribed, redirect to dashboard
    useEffect(() => {
        if (subscriptionStatus === 'active' || subscriptionStatus === 'trialing') {
            navigate('/dashboard', { replace: true });
        }
    }, [subscriptionStatus, navigate]);

    // Safety net: if we have a session but no business id, try to refetch
    useEffect(() => {
        if (session?.user?.id && !business?.id && !authLoading) {
            console.log('[Pricing] Business missing, triggering refetch...');
            refetchBusiness(session.user.id);
        }
    }, [session, business, authLoading, refetchBusiness]);

    // If user presses back, sign them out and send to signup
    useEffect(() => {
        const handleBack = async () => {
            await signOut();
            navigate('/login', { replace: true });
        };
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', handleBack);
        return () => window.removeEventListener('popstate', handleBack);
    }, [signOut, navigate]);

    // Handle return from Polar checkout (success URL)
    useEffect(() => {
        const checkoutStatus = searchParams.get('checkout');
        if (checkoutStatus === 'success' && business?.id) {
            console.log('[Polar] Checkout success — activating subscription...');
            setIsProcessingPayment(true);

            (async () => {
                try {
                    await api.activateSubscription(business.id);
                    console.log('[Polar] Activation successful.');
                } catch (err) {
                    console.warn('[Polar] Activation failed, waiting for webhook:', err.message);
                }

                // Poll for DB update
                const maxAttempts = 15;
                for (let i = 0; i < maxAttempts; i++) {
                    await new Promise(r => setTimeout(r, 2000));
                    if (refetchBusiness) await refetchBusiness();

                    const { data } = await supabase
                        .from('businesses')
                        .select('subscription_status')
                        .eq('user_id', session?.user?.id)
                        .single();

                    if (data?.subscription_status === 'active' || data?.subscription_status === 'trialing') {
                        console.log('[Polar] Subscription active in DB!');
                        return; // useEffect above will redirect to dashboard
                    }
                    console.log(`[Polar] Attempt ${i + 1}/${maxAttempts} — status: ${data?.subscription_status}`);
                }

                setIsProcessingPayment(false);
                alert('We received your payment, but the setup is taking longer than expected. Please wait a moment and refresh the page to access your dashboard.');
            })();
        }
    }, [searchParams, business, session, refetchBusiness]);

    const handleCheckout = async (productId) => {
        if (!business?.id) {
            console.error('[Polar] Business ID still loading or missing');
            alert(t('err_biz_loading'));
            return;
        }

        if (checkoutLoading) return; // Prevent double clicks
        setCheckoutLoading(true);
        setCheckoutError('');

        try {
            console.log('[Polar] Creating checkout:', { productId, businessId: business.id });
            const { url } = await api.createCheckout(business.id, productId, lang);

            if (url) {
                window.location.href = url; // Redirect to Polar checkout
            } else {
                throw new Error('No checkout URL returned');
            }
        } catch (err) {
            console.error('[Polar] Checkout error:', err.message);
            setCheckoutError(err.message || 'Failed to start checkout. Please try again.');
            setCheckoutLoading(false); // Only re-enable on error (success redirects away)
        }
    };

    const monthlyPrice = 9.99;
    const yearlyPrice = 89.99;
    const savings = (monthlyPrice * 12 - yearlyPrice).toFixed(2);

    if (isProcessingPayment) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text)' }}>
                <Loader2 size={48} className="spin" color="var(--primary)" style={{ marginBottom: 24 }} />
                <h2 style={{ fontSize: 24, fontWeight: '700', marginBottom: 12 }}>{t('processing_payment') || 'Setting up your subscription...'}</h2>
                <p style={{ color: 'var(--text-muted)' }}>{t('processing_payment_desc') || 'Please wait a few seconds while we verify your payment.'}</p>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg)', color: 'var(--text)', position: 'relative', overflow: 'hidden' }}>
            <div className="bg-shapes hide-mobile">
                <div className="shape shape-1" style={{ top: '-10%', left: '-10%', width: 600, height: 600, opacity: 0.15 }}></div>
                <div className="shape shape-2" style={{ bottom: '-10%', right: '-10%', width: 800, height: 800, opacity: 0.1 }}></div>
            </div>

            {/* Header with Language Toggle */}
            <header className="header-container" style={{ position: 'relative', zIndex: 10 }}>
                <Link to="/" className="btn btn-outline glass-btn header-back-minimal" style={{ textDecoration: 'none' }}>
                    <ArrowLeft size={18} /> <span className="hide-mobile" style={{ marginLeft: 8 }}>{t('back_to_home')}</span>
                </Link>

                <div className="header-centered-logo">
                    <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none' }}>
                        <Logo size={24} />
                        <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)', letterSpacing: -0.5 }}>Wafiyo</span>
                    </Link>
                </div>

                <LanguageToggle />
            </header>

            <div style={{ flex: 1, padding: '60px 20px', maxWidth: 1000, margin: '0 auto', width: '100%', position: 'relative', zIndex: 1 }}>
                {/* Migration Banner for existing Paddle users */}
                {needsMigration && (
                    <div style={{
                        background: 'rgba(245, 158, 11, 0.08)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: 16,
                        padding: '20px 24px',
                        marginBottom: 32,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 16,
                        maxWidth: 700,
                        margin: '0 auto 32px'
                    }}>
                        <AlertTriangle size={24} color="#f59e0b" style={{ flexShrink: 0, marginTop: 2 }} />
                        <div>
                            <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 6, color: 'var(--text)' }}>
                                {t('migration_pricing_title') || 'Payment provider update'}
                            </p>
                            <p style={{ fontSize: 14, color: 'var(--text-dim)', lineHeight: 1.5, margin: 0 }}>
                                {t('migration_pricing_desc') || 'We\'ve switched to a new payment provider. Please select a plan below to continue using Wafiyo. Your free trial has already been used, so billing will start immediately.'}
                            </p>
                        </div>
                    </div>
                )}

                <div style={{ textAlign: 'center', marginBottom: 60 }}>
                    <h1 style={{ fontSize: 'clamp(32px, 5vw, 48px)', fontWeight: 800, marginBottom: 16, letterSpacing: -1 }}>
                        {t('choose_your_plan')}
                    </h1>
                    <p style={{ fontSize: 18, color: 'var(--text-dim)', maxWidth: 600, margin: '0 auto' }}>
                        {t('pricing_subtitle')}
                    </p>
                </div>

                {checkoutError && (
                    <div style={{
                        background: 'rgba(239, 68, 68, 0.08)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: 12,
                        padding: '12px 20px',
                        marginBottom: 24,
                        textAlign: 'center',
                        color: '#ef4444',
                        fontSize: 14,
                        maxWidth: 600,
                        margin: '0 auto 24px'
                    }}>
                        {checkoutError}
                    </div>
                )}

                <div className="pricing-grid">
                    {/* Monthly Plan */}
                    <div className="step-card glass-card" style={{ display: 'flex', flexDirection: 'column', padding: 40, position: 'relative', overflow: 'visible' }}>
                        {!needsMigration && !isReturningUser && (
                            <div style={{
                                position: 'absolute',
                                top: -14,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                background: 'linear-gradient(135deg, #10b981, #059669)',
                                padding: '6px 16px',
                                borderRadius: 20,
                                fontSize: 12,
                                fontWeight: 800,
                                color: '#fff',
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 6,
                                boxShadow: '0 4px 12px rgba(16,185,129,0.3)'
                            }}>
                                🎁 {t('free_trial_badge')}
                            </div>
                        )}
                        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12, marginTop: 8 }}>{t('monthly_plan')}</h2>
                        <div style={{ marginBottom: 32 }}>
                            {!needsMigration && !isReturningUser ? (
                                <>
                                    <span style={{ fontSize: 48, fontWeight: 800 }}>{currencySymbol}0</span>
                                    <span style={{ color: 'var(--text-dim)', fontSize: 16 }}> / {t('first_month')}</span>
                                    <div
                                        style={{ fontSize: 14, color: 'var(--text-dim)', marginTop: 6, fontWeight: 500 }}
                                        dangerouslySetInnerHTML={{ __html: t('then_price', { symbol: currencySymbol, amount: `9<span style="font-size: 0.75em;">.99</span>` }) }}
                                    />
                                </>
                            ) : (
                                <>
                                    <span style={{ fontSize: 48, fontWeight: 800 }}>{currencySymbol}9<span style={{ fontSize: '0.6em' }}>.99</span></span>
                                    <span style={{ color: 'var(--text-dim)', fontSize: 16 }}>/{t('month') || 'month'}</span>
                                </>
                            )}
                        </div>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: '0 auto 40px',
                            color: 'var(--text-dim)',
                            flex: 1,
                            width: 'fit-content',
                            maxWidth: '100%',
                            textAlign: 'left'
                        }}>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('unlimited_clients')}</span>
                            </li>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('custom_card_designs')}</span>
                            </li>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('qr_code_scanning')}</span>
                            </li>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('google_wallet_integration')}</span>
                            </li>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('push_notifications_feature')}</span>
                            </li>
                        </ul>
                        <button
                            className="btn btn-primary btn-lg"
                            style={{ width: '100%', opacity: (businessReady && !checkoutLoading) ? 1 : 0.5, height: 54 }}
                            onClick={() => handleCheckout(process.env.REACT_APP_POLAR_MONTHLY_PRODUCT_ID)}
                            disabled={!businessReady || checkoutLoading}
                        >
                            {checkoutLoading ? (t('processing') || 'Processing...') : businessReady ? ((needsMigration || isReturningUser) ? (t('choose_monthly') || 'Choose Monthly') : t('start_free_trial')) : 'Loading...'}
                        </button>
                    </div>

                    {/* Yearly Plan */}
                    <div className="step-card glass-card featured-plan" style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 40,
                        position: 'relative',
                        background: 'rgba(26, 122, 74, 0.03)',
                        borderColor: 'var(--primary)',
                        boxShadow: '0 8px 32px rgba(26, 122, 74, 0.08)'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: -14,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'var(--primary)',
                            padding: '6px 16px',
                            borderRadius: 20,
                            fontSize: 12,
                            fontWeight: 800,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            color: '#fff'
                        }}>
                            {t('best_value')}
                        </div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 12 }}>{t('yearly_plan')}</h2>
                        <div style={{ marginBottom: 8 }}>
                            <span style={{ fontSize: 48, fontWeight: 800 }}>{currencySymbol}89<span style={{ fontSize: '0.6em' }}>.99</span></span>
                            <span style={{ color: 'var(--text-dim)', fontSize: 16 }}>/{t('year')}</span>
                        </div>
                        <p style={{ color: '#4ade80', fontWeight: 600, fontSize: 14, marginBottom: 24 }}>
                            {t('save_amount', { amount: savings, symbol: currencySymbol })}
                        </p>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: '0 auto 40px',
                            color: 'var(--text-dim)',
                            flex: 1,
                            width: 'fit-content',
                            maxWidth: '100%',
                            textAlign: 'left'
                        }}>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('all_monthly_features')}</span>
                            </li>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('three_months_free')}</span>
                            </li>
                            <li style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                                <Check size={18} color="var(--primary)" style={{ marginTop: 2, flexShrink: 0 }} />
                                <span style={{ lineHeight: '1.4' }}>{t('priority_support')}</span>
                            </li>
                        </ul>
                        <button
                            className="btn btn-primary btn-lg btn-glow"
                            style={{ width: '100%', opacity: (businessReady && !checkoutLoading) ? 1 : 0.5, height: 54 }}
                            onClick={() => handleCheckout(process.env.REACT_APP_POLAR_YEARLY_PRODUCT_ID)}
                            disabled={!businessReady || checkoutLoading}
                        >
                            {checkoutLoading ? (t('processing') || 'Processing...') : businessReady ? t('choose_yearly') : 'Loading...'}
                        </button>
                    </div>
                </div>

                <p style={{ textAlign: 'center', marginTop: 40, color: 'var(--text-dim)', fontSize: 14 }}>
                    {t('pricing_footer_text')}
                </p>
                <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 60 }}>
                    <button
                        onClick={signOut}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--text-muted)',
                            fontSize: 13,
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            opacity: 0.7
                        }}
                    >
                        {t('sign_out') || 'Sign out'}
                    </button>
                </div>
            </div>


            {/* Standard Footer */}
            <footer className="landing-footer" style={{ borderTop: '1px solid var(--border)' }}>
                <div className="container">
                    <div className="footer-content">
                        <div className="footer-brand">
                            <Logo size={28} />
                            <span className="logo-text">Wafiyo</span>
                            <p className="footer-tagline">
                                {t('footer_tagline')}
                            </p>
                        </div>
                        <div className="footer-links">
                            <div className="link-col">
                                <h4>{t('legal')}</h4>
                                <Link to="/privacy">{t('privacy_policy_title')}</Link>
                                <Link to="/terms">{t('tos_title')}</Link>
                                <Link to="/refund">{t('refund_policy_title')}</Link>
                                <Link to="/contact">{t('contact_title')}</Link>
                            </div>
                        </div>
                    </div>
                    <div className="footer-bottom">
                        <p>© {new Date().getFullYear()} Wafiyo. {t('all_rights_reserved')}</p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
